import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  TimePicker,
  Typography,
  message,
} from 'antd';
import Editor from '@monaco-editor/react';
import dayjs from 'dayjs';
import mjml2html from 'mjml-browser';
import type * as MonacoEditor from 'monaco-editor';
import { useNavigate, useParams } from 'react-router-dom';
import MailingService, {
  CreateMailingCampaignRequest,
  CreateMailingTemplateRequest,
  MailingCampaign,
  MailingContact,
  MailingLog,
  MailingTemplate,
  StartMailingCampaignRequest,
  UpdateMailingTemplateRequest,
} from '../../services/MailingService';
import { hasRole } from '../../utils';
import NotFoundPage from '../NotFound';
import MailingCampaignsTab from './MailingCampaignsTab';
import MailingContactsTab from './MailingContactsTab';
import MailingTemplatesTab from './MailingTemplatesTab';
import { getCampaignStatusLabel, getMailingLogStatusLabel, getMailingSourceLabel } from './mailingLabels';
import SurveyAdmin from './SurveyAdmin';

const { Title, Text } = Typography;

const TAB_KEYS = ['contacts', 'templates', 'campaigns', 'surveys'] as const;
type TabKey = (typeof TAB_KEYS)[number];
type CampaignContactSourceFilter = 'ALL' | 'MANUAL' | 'IMPORT';

type StartCampaignFormValues = {
  startDate: dayjs.Dayjs;
  sendWindow: [dayjs.Dayjs, dayjs.Dayjs];
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm:ss') : '-';
};

const formatTime = (value?: string | null) => {
  if (!value) return '-';
  return value.length >= 5 ? value.slice(0, 5) : value;
};

const formatTimeRange = (start?: string | null, end?: string | null) => {
  if (!start || !end) return '-';
  return `${formatTime(start)} - ${formatTime(end)}`;
};

const formatContactName = (firstName?: string | null, lastName?: string | null) => {
  const fullName = `${firstName || ''} ${lastName || ''}`.trim();
  return fullName || '-';
};

const parseTimeToDayjs = (value?: string | null) => {
  if (!value) return null;
  const normalized = value.length === 5 ? `${value}:00` : value;
  const parsed = dayjs(`2000-01-01T${normalized}`);
  return parsed.isValid() ? parsed : null;
};

const campaignStatusColor = (status?: string) => {
  if (status === 'DRAFT') return 'default';
  if (status === 'SENDING') return 'processing';
  if (status === 'PAUSED') return 'warning';
  if (status === 'COMPLETED') return 'success';
  if (status === 'CANCELLED') return 'error';
  return 'default';
};

const getSafeTab = (tabParam?: string): TabKey => {
  if (tabParam && TAB_KEYS.includes(tabParam as TabKey)) {
    return tabParam as TabKey;
  }
  return 'contacts';
};

const getErrorMessage = (error: any, fallback: string) => {
  return error?.response?.data?.message || fallback;
};

const TEMPLATE_VARIABLES = [
  '[firstName]',
  '[lastName]',
  '[email]',
  '[phone]',
  '[companyName]',
  '[id]',
  '[surveyLink]',
  '[surveyName]',
  '[responseDeadline]',
] as const;

const applyTemplatePreviewData = (content: string) => {
  if (!content) return '';
  return content
    .replaceAll('[firstName]', 'Ada')
    .replaceAll('[lastName]', 'Lovelace')
    .replaceAll('[email]', 'ada@example.com')
    .replaceAll('[phone]', '+90 555 123 45 67')
    .replaceAll('[companyName]', 'Analytical Engines')
    .replaceAll('[id]', '9f56b469-e433-4cd7-9989-5f8a6d0f6a14')
    .replaceAll('{{first_name}}', 'Ada')
    .replaceAll('{{last_name}}', 'Lovelace')
    .replaceAll('{{email}}', 'ada@example.com')
    .replaceAll('{{company_name}}', 'Analytical Engines')
    .replaceAll('[surveyLink]', 'https://app.example.com/public/mailing-survey?token=sample')
    .replaceAll('[surveyName]', 'Müşteri Memnuniyet Anketi')
    .replaceAll('[responseDeadline]', '31.12.2026 18:00');
};

const MailingAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const activeTab = useMemo(() => getSafeTab(tab), [tab]);

  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState<MailingContact[]>([]);
  const [templates, setTemplates] = useState<MailingTemplate[]>([]);
  const [campaigns, setCampaigns] = useState<MailingCampaign[]>([]);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MailingTemplate | null>(null);
  const [campaignContactSearch, setCampaignContactSearch] = useState('');
  const [campaignContactSourceFilter, setCampaignContactSourceFilter] = useState<CampaignContactSourceFilter>('ALL');
  const [campaignTagFilters, setCampaignTagFilters] = useState<string[]>([]);

  const [isStartCampaignModalOpen, setIsStartCampaignModalOpen] = useState(false);
  const [startCampaignTarget, setStartCampaignTarget] = useState<MailingCampaign | null>(null);

  const [isLogDrawerOpen, setIsLogDrawerOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<MailingCampaign | null>(null);
  const [campaignLogs, setCampaignLogs] = useState<MailingLog[]>([]);
  const [logLoading, setLogLoading] = useState(false);

  const [updatingTemplateStatusId, setUpdatingTemplateStatusId] = useState<string | null>(null);

  const [templateForm] = Form.useForm<CreateMailingTemplateRequest>();
  const [campaignForm] = Form.useForm<CreateMailingCampaignRequest>();
  const [startCampaignForm] = Form.useForm<StartCampaignFormValues>();
  const [templateEditorValue, setTemplateEditorValue] = useState('');
  const templateEditorRef = useRef<MonacoEditor.editor.IStandaloneCodeEditor | null>(null);
  const selectedCampaignContactIds = Form.useWatch('contactIds', campaignForm) as string[] | undefined;

  const isAdmin = hasRole('ADMIN');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [contactsResp, templatesResp, campaignsResp] = await Promise.all([
        MailingService.getContacts(true),
        MailingService.getTemplates(true),
        MailingService.getCampaigns(),
      ]);
      setContacts(Array.isArray(contactsResp?.data) ? contactsResp.data : []);
      setTemplates(Array.isArray(templatesResp?.data) ? templatesResp.data : []);
      setCampaigns(Array.isArray(campaignsResp?.data) ? campaignsResp.data : []);
    } catch (error) {
      console.error('Mailing data could not be loaded', error);
      message.error(getErrorMessage(error, 'Mailing verileri yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCampaignLogs = useCallback(async (campaignId: string) => {
    setLogLoading(true);
    try {
      const response = await MailingService.getCampaignLogs(campaignId);
      setCampaignLogs(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Campaign logs could not be loaded', error);
      message.error(getErrorMessage(error, 'Kampanya logları yüklenemedi'));
      setCampaignLogs([]);
    } finally {
      setLogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!tab || !TAB_KEYS.includes(tab as TabKey)) {
      navigate('/mailing/contacts', { replace: true });
    }
  }, [tab, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedCampaign) return;
    const updated = campaigns.find((c) => c.id === selectedCampaign.id);
    if (updated) {
      setSelectedCampaign(updated);
    }
  }, [campaigns, selectedCampaign]);

  const openTemplateModal = (template?: MailingTemplate) => {
    if (template) {
      setEditingTemplate(template);
      templateForm.setFieldsValue({
        name: template.name,
        subject: template.subject,
        content: template.content,
      });
      setTemplateEditorValue(template.content || '');
    } else {
      setEditingTemplate(null);
      templateForm.resetFields();
      setTemplateEditorValue('');
    }
    setIsTemplateModalOpen(true);
  };

  const closeTemplateModal = () => {
    setIsTemplateModalOpen(false);
    setEditingTemplate(null);
    templateForm.resetFields();
    setTemplateEditorValue('');
    templateEditorRef.current = null;
  };

  const handleTemplateEditorChange = (value?: string) => {
    const nextValue = value || '';
    setTemplateEditorValue(nextValue);
    templateForm.setFieldsValue({ content: nextValue });
  };

  const handleTemplateEditorMount = (editor: MonacoEditor.editor.IStandaloneCodeEditor) => {
    templateEditorRef.current = editor;
  };

  const insertTemplateVariable = (token: string) => {
    const editor = templateEditorRef.current;
    if (!editor) {
      const nextValue = `${templateEditorValue}${token}`;
      setTemplateEditorValue(nextValue);
      templateForm.setFieldsValue({ content: nextValue });
      return;
    }

    const selection = editor.getSelection();
    const model = editor.getModel();
    if (!selection || !model) {
      const nextValue = `${editor.getValue()}${token}`;
      editor.setValue(nextValue);
      setTemplateEditorValue(nextValue);
      templateForm.setFieldsValue({ content: nextValue });
      return;
    }

    editor.executeEdits('template-variable', [
      {
        range: selection,
        text: token,
        forceMoveMarkers: true,
      },
    ]);
    editor.focus();
    const nextValue = editor.getValue();
    setTemplateEditorValue(nextValue);
    templateForm.setFieldsValue({ content: nextValue });
  };

  const templatePreviewDoc = useMemo(() => {
    const source = applyTemplatePreviewData(templateEditorValue || '');
    if (!source.trim()) {
      return '<!doctype html><html><body style="font-family:Arial,sans-serif;padding:16px;color:#666;">Canlı önizleme burada görünecek.</body></html>';
    }

    if (source.toLowerCase().includes('<mjml')) {
      try {
        const result = mjml2html(source, { validationLevel: 'soft' });
        return result.html || '<html><body>MJML dönüştürülemedi.</body></html>';
      } catch (error) {
        return `<html><body style="font-family:monospace;padding:16px;color:#b00020;">MJML hata: ${String(error)}</body></html>`;
      }
    }
    return source;
  }, [templateEditorValue]);

  const handleSaveTemplate = async () => {
    try {
      if (!templateEditorValue.trim()) {
        message.error('İçerik zorunludur');
        return;
      }
      templateForm.setFieldsValue({ content: templateEditorValue });
      const values = await templateForm.validateFields();
      if (editingTemplate) {
        await MailingService.updateTemplate(editingTemplate.id, values as UpdateMailingTemplateRequest);
        message.success('Şablon güncellendi');
      } else {
        await MailingService.createTemplate(values);
        message.success('Şablon eklendi');
      }
      closeTemplateModal();
      await loadData();
    } catch (error) {
      if (error) {
        console.error('Template save failed', error);
        message.error(getErrorMessage(error, 'Şablon kaydedilemedi'));
      }
    }
  };

  const handleSetTemplateStatus = async (templateId: string, active: boolean) => {
    try {
      setUpdatingTemplateStatusId(templateId);
      await MailingService.updateTemplateStatus(templateId, active);
      message.success(active ? 'Şablon aktife alındı' : 'Şablon pasife alındı');
      await loadData();
    } catch (error) {
      console.error('Template status update failed', error);
      message.error(getErrorMessage(error, 'Şablon durumu güncellenemedi'));
    } finally {
      setUpdatingTemplateStatusId(null);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const values = await campaignForm.validateFields();
      await MailingService.createCampaign(values);
      message.success('Kampanya eklendi');
      closeCampaignModal();
      await loadData();
    } catch (error) {
      if (error) {
        console.error('Campaign create failed', error);
      }
    }
  };

  const openStartCampaignModal = (campaign: MailingCampaign) => {
    const defaultWindowStart = dayjs().hour(9).minute(0).second(0);
    const defaultWindowEnd = dayjs().hour(18).minute(0).second(0);
    const scheduledStart = campaign.scheduledStartAt ? dayjs(campaign.scheduledStartAt) : null;

    setStartCampaignTarget(campaign);
    startCampaignForm.setFieldsValue({
      startDate: scheduledStart && scheduledStart.isValid() ? scheduledStart : dayjs(),
      sendWindow: [
        parseTimeToDayjs(campaign.sendWindowStartTime) || defaultWindowStart,
        parseTimeToDayjs(campaign.sendWindowEndTime) || defaultWindowEnd,
      ],
    });
    setIsStartCampaignModalOpen(true);
  };

  const closeStartCampaignModal = () => {
    setIsStartCampaignModalOpen(false);
    setStartCampaignTarget(null);
    startCampaignForm.resetFields();
  };

  const handleStartCampaign = async () => {
    try {
      if (!startCampaignTarget) {
        return;
      }

      const values = await startCampaignForm.validateFields();
      const payload: StartMailingCampaignRequest = {
        startDate: values.startDate.format('YYYY-MM-DD'),
        sendWindowStartTime: values.sendWindow[0].format('HH:mm:ss'),
        sendWindowEndTime: values.sendWindow[1].format('HH:mm:ss'),
      };

      await MailingService.startCampaign(startCampaignTarget.id, payload);
      message.success('Kampanya başlatıldı');
      const campaignId = startCampaignTarget.id;
      closeStartCampaignModal();
      loadData();
      if (isLogDrawerOpen && selectedCampaign?.id === campaignId) {
        loadCampaignLogs(campaignId);
      }
    } catch (error) {
      if ((error as { errorFields?: unknown[] })?.errorFields) {
        return;
      }
      console.error('Campaign start failed', error);
      message.error(getErrorMessage(error, 'Kampanya başlatılamadı'));
    }
  };

  const handleStopCampaign = async (campaignId: string) => {
    try {
      await MailingService.stopCampaign(campaignId);
      message.success('Kampanya durduruldu');
      loadData();
      if (isLogDrawerOpen && selectedCampaign?.id === campaignId) {
        loadCampaignLogs(campaignId);
      }
    } catch (error) {
      console.error('Campaign stop failed', error);
      message.error(getErrorMessage(error, 'Kampanya durdurulamadı'));
    }
  };

  const handleCancelCampaign = async (campaignId: string) => {
    try {
      await MailingService.cancelCampaign(campaignId);
      message.success('Kampanya iptal edildi');
      loadData();
      if (isLogDrawerOpen && selectedCampaign?.id === campaignId) {
        loadCampaignLogs(campaignId);
      }
    } catch (error) {
      console.error('Campaign cancel failed', error);
      message.error(getErrorMessage(error, 'Kampanya iptal edilemedi'));
    }
  };

  const openLogDrawer = (campaign: MailingCampaign) => {
    setSelectedCampaign(campaign);
    setIsLogDrawerOpen(true);
    loadCampaignLogs(campaign.id);
  };

  const openCampaignModal = () => {
    campaignForm.resetFields();
    campaignForm.setFieldsValue({ delayMinutes: 0, contactIds: [] });
    setCampaignContactSearch('');
    setCampaignContactSourceFilter('ALL');
    setCampaignTagFilters([]);
    setIsCampaignModalOpen(true);
  };

  const closeCampaignModal = () => {
    setIsCampaignModalOpen(false);
    campaignForm.resetFields();
    setCampaignContactSearch('');
    setCampaignContactSourceFilter('ALL');
    setCampaignTagFilters([]);
  };

  const activeContacts = useMemo(() => contacts.filter((contact) => contact.active !== false), [contacts]);
  const activeTemplates = useMemo(() => templates.filter((template) => template.active !== false), [templates]);
  const campaignTagOptions = useMemo(() => {
    const tags = new Set<string>();
    activeContacts.forEach((contact) => {
      (contact.tags || []).forEach((tag) => tags.add(tag));
    });
    return Array.from(tags).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [activeContacts]);
  const filteredCampaignContacts = useMemo(() => {
    const normalizedSearch = campaignContactSearch.trim().toLowerCase();
    return activeContacts.filter((contact) => {
      const contactSource = contact.source || 'MANUAL';
      if (campaignContactSourceFilter !== 'ALL' && contactSource !== campaignContactSourceFilter) {
        return false;
      }

      const contactTags = contact.tags || [];
      if (campaignTagFilters.length > 0 && !contactTags.some((tag) => campaignTagFilters.includes(tag))) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchable = [
        contact.firstName || '',
        contact.lastName || '',
        contact.email || '',
        contact.phone || '',
        contact.companyName || '',
      ]
        .join(' ')
        .toLowerCase();
      return searchable.includes(normalizedSearch);
    });
  }, [activeContacts, campaignContactSearch, campaignContactSourceFilter, campaignTagFilters]);
  const contactById = useMemo(() => {
    const map = new Map<string, MailingContact>();
    contacts.forEach((contact) => map.set(contact.id, contact));
    return map;
  }, [contacts]);
  const selectedCampaignContacts = useMemo(() => {
    if (!selectedCampaign) {
      return [] as MailingContact[];
    }
    const ids = Array.isArray(selectedCampaign.contactIds) ? selectedCampaign.contactIds : [];
    if (!ids.length) {
      return activeContacts;
    }
    return ids
      .map((id) => contactById.get(id))
      .filter((contact): contact is MailingContact => Boolean(contact));
  }, [activeContacts, contactById, selectedCampaign]);

  const nextContact =
    selectedCampaign && selectedCampaign.currentIndex >= 0 && selectedCampaign.currentIndex < selectedCampaignContacts.length
      ? selectedCampaignContacts[selectedCampaign.currentIndex]
      : null;

  if (!isAdmin) {
    return <NotFoundPage />;
  }

  return (
    <div className='mb-120px'>
      <Card className='br-30px kontrolx-card mailing-admin-card'>
        <Space direction='vertical' size={16} style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Mailing Yönetimi
          </Title>
          <Tabs
            className='custom-tabs'
            activeKey={activeTab}
            onChange={(key) => navigate(`/mailing/${key}`)}
            items={[
              {
                key: 'contacts',
                label: (
                  <Button type={activeTab === 'contacts' ? 'primary' : 'default'} className='d-button tab-button'>
                    Kişiler
                  </Button>
                ),
                children: <MailingContactsTab contacts={contacts} loading={loading} onReload={loadData} />,
              },
              {
                key: 'templates',
                label: (
                  <Button type={activeTab === 'templates' ? 'primary' : 'default'} className='d-button tab-button'>
                    Şablonlar
                  </Button>
                ),
                children: (
                  <MailingTemplatesTab
                    templates={templates}
                    loading={loading}
                    updatingTemplateStatusId={updatingTemplateStatusId}
                    onOpenTemplateModal={openTemplateModal}
                    onToggleTemplateStatus={handleSetTemplateStatus}
                  />
                ),
              },
              {
                key: 'campaigns',
                label: (
                  <Button type={activeTab === 'campaigns' ? 'primary' : 'default'} className='d-button tab-button'>
                    Kampanyalar
                  </Button>
                ),
                children: (
                  <MailingCampaignsTab
                    campaigns={campaigns}
                    loading={loading}
                    onOpenCampaignModal={openCampaignModal}
                    onOpenLogDrawer={openLogDrawer}
                    onOpenStartCampaignModal={openStartCampaignModal}
                    onStopCampaign={handleStopCampaign}
                    onCancelCampaign={handleCancelCampaign}
                    formatDateTime={formatDateTime}
                    formatTimeRange={formatTimeRange}
                    campaignStatusColor={campaignStatusColor}
                  />
                ),
              },
              {
                key: 'surveys',
                label: (
                  <Button type={activeTab === 'surveys' ? 'primary' : 'default'} className='d-button tab-button'>
                    Anket
                  </Button>
                ),
                children: <SurveyAdmin contacts={contacts} templates={templates} />,
              },
            ]}
          />
        </Space>
      </Card>

      <Drawer
        title={selectedCampaign ? `${selectedCampaign.name} - Kampanya Detayı` : 'Kampanya Detayı'}
        placement='right'
        width={760}
        open={isLogDrawerOpen}
        onClose={() => setIsLogDrawerOpen(false)}
        extra={
          selectedCampaign ? (
            <Button onClick={() => loadCampaignLogs(selectedCampaign.id)} loading={logLoading}>
              Yenile
            </Button>
          ) : null
        }
      >
        {selectedCampaign && (
          <Space direction='vertical' size={16} style={{ width: '100%' }}>
            <Descriptions bordered size='small' column={1}>
              <Descriptions.Item label='Durum'>
                <Tag color={campaignStatusColor(selectedCampaign.status)}>{getCampaignStatusLabel(selectedCampaign.status)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label='Toplam Kişi'>
                {selectedCampaign.recipientCount ?? selectedCampaignContacts.length}
              </Descriptions.Item>
              <Descriptions.Item label='Mevcut Sıra'>{selectedCampaign.currentIndex}</Descriptions.Item>
              <Descriptions.Item label='Planlanan Başlangıç'>{formatDateTime(selectedCampaign.scheduledStartAt)}</Descriptions.Item>
              <Descriptions.Item label='Saat Aralığı'>
                {formatTimeRange(selectedCampaign.sendWindowStartTime, selectedCampaign.sendWindowEndTime)}
              </Descriptions.Item>
              <Descriptions.Item label='Planlanan Bitiş'>{formatDateTime(selectedCampaign.plannedEndAt)}</Descriptions.Item>
              <Descriptions.Item label='Sıradaki Kişi'>
                {nextContact ? `${formatContactName(nextContact.firstName, nextContact.lastName)} (${nextContact.email})` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label='Son Gönderim'>{formatDateTime(selectedCampaign.lastSentAt)}</Descriptions.Item>
            </Descriptions>

            <Table<MailingLog>
              rowKey='id'
              loading={logLoading}
              dataSource={campaignLogs}
              pagination={{ pageSize: 8 }}
              columns={[
                {
                  title: 'Kişi',
                  key: 'contact',
                  render: (_: unknown, record) => {
                    const name = formatContactName(record.contact?.firstName, record.contact?.lastName);
                    const email = record.contact?.email || '-';
                    return `${name} / ${email}`;
                  },
                },
                {
                  title: 'Durum',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status: string) => (
                    <Tag color={status === 'SENT' ? 'success' : status === 'SKIPPED' ? 'warning' : 'error'}>
                      {getMailingLogStatusLabel(status)}
                    </Tag>
                  ),
                },
                {
                  title: 'Gönderim Zamanı',
                  dataIndex: 'sentAt',
                  key: 'sentAt',
                  render: (v?: string | null) => formatDateTime(v),
                },
                {
                  title: 'Hata',
                  dataIndex: 'errorMessage',
                  key: 'errorMessage',
                  render: (v?: string | null) => v || '-',
                },
              ]}
            />
          </Space>
        )}
      </Drawer>

      <Modal
        title={editingTemplate ? 'Şablon Düzenle' : 'Şablon Ekle'}
        open={isTemplateModalOpen}
        onCancel={closeTemplateModal}
        onOk={handleSaveTemplate}
        okText={editingTemplate ? 'Güncelle' : 'Kaydet'}
        width={1280}
      >
        <Form layout='vertical' form={templateForm}>
          <Form.Item name='name' label='Ad' rules={[{ required: true, message: 'Ad zorunludur' }]}>
            <Input />
          </Form.Item>
          <Form.Item name='subject' label='Konu' rules={[{ required: true, message: 'Konu zorunludur' }]}>
            <Input />
          </Form.Item>
          <Form.Item name='content' label='İçerik' rules={[{ required: true, message: 'İçerik zorunludur' }]} hidden>
            <Input />
          </Form.Item>
          <Space direction='vertical' size={12} style={{ width: '100%' }}>
            <Space wrap>
              <Text strong>Özel Alanlar:</Text>
              {TEMPLATE_VARIABLES.map((token) => (
                <Button key={token} size='small' onClick={() => insertTemplateVariable(token)}>
                  {token}
                </Button>
              ))}
            </Space>
            <Text type='secondary'>Monaco editörde MJML veya HTML yazabilirsiniz. Sağ panel canlı önizlemeyi gösterir.</Text>
            <a href='https://mjml.io/templates' target='_blank' rel='noreferrer'>
              MJML Template Örnekleri
            </a>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: 12,
                alignItems: 'start',
              }}
            >
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                <Editor
                  height='420px'
                  defaultLanguage='html'
                  value={templateEditorValue}
                  onChange={handleTemplateEditorChange}
                  onMount={handleTemplateEditorMount}
                  options={{
                    minimap: { enabled: false },
                    wordWrap: 'on',
                    fontSize: 13,
                    automaticLayout: true,
                  }}
                />
              </div>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                <iframe
                  title='template-preview'
                  srcDoc={templatePreviewDoc}
                  style={{ width: '100%', height: 420, border: 0, background: '#fff' }}
                />
              </div>
            </div>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={startCampaignTarget ? `${startCampaignTarget.name} - Kampanya Başlat` : 'Kampanya Başlat'}
        open={isStartCampaignModalOpen}
        onCancel={closeStartCampaignModal}
        onOk={handleStartCampaign}
        okText='Başlat'
      >
        <Form layout='vertical' form={startCampaignForm}>
          <Form.Item name='startDate' label='Başlangıç Tarihi' rules={[{ required: true, message: 'Başlangıç tarihi seçiniz' }]}>
            <DatePicker style={{ width: '100%' }} format='DD.MM.YYYY' />
          </Form.Item>
          <Form.Item
            name='sendWindow'
            label='Saat Aralığı'
            rules={[
              { required: true, message: 'Saat aralığı seçiniz' },
              {
                validator: (_, value: [dayjs.Dayjs, dayjs.Dayjs] | undefined) => {
                  if (!value || value.length !== 2) {
                    return Promise.reject(new Error('Saat aralığı seçiniz'));
                  }
                  if (!value[0].isBefore(value[1])) {
                    return Promise.reject(new Error('Başlangıç saati bitiş saatinden önce olmalıdır'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TimePicker.RangePicker format='HH:mm' minuteStep={5} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title='Kampanya Ekle'
        open={isCampaignModalOpen}
        onCancel={closeCampaignModal}
        onOk={handleCreateCampaign}
        okText='Kaydet'
        width={1100}
      >
        <Form layout='vertical' form={campaignForm} initialValues={{ delayMinutes: 0, contactIds: [] }}>
          <Form.Item name='name' label='Ad' rules={[{ required: true, message: 'Ad zorunludur' }]}>
            <Input />
          </Form.Item>
          <Form.Item name='templateId' label='Şablon' rules={[{ required: true, message: 'Şablon seçiniz' }]}>
            <Select placeholder='Şablon seçiniz' options={activeTemplates.map((t) => ({ label: t.name, value: t.id }))} />
          </Form.Item>
          <Form.Item
            name='contactIds'
            rules={[{ required: true, message: 'En az bir kişi seçiniz' }]}
            hidden
          >
            <Select mode='multiple' options={[]} />
          </Form.Item>
          <Form.Item label='Kişiler (Aktif)'>
            <Space direction='vertical' size={12} style={{ width: '100%' }}>
              <Space wrap>
                <Input.Search
                  allowClear
                  style={{ width: 340 }}
                  placeholder='Ad, soyad, telefon, e-posta, firma ara'
                  value={campaignContactSearch}
                  onChange={(event) => setCampaignContactSearch(event.target.value)}
                />
                <Select<CampaignContactSourceFilter>
                  style={{ width: 170 }}
                  value={campaignContactSourceFilter}
                  onChange={setCampaignContactSourceFilter}
                  options={[
                    { label: 'Kaynak: Tümü', value: 'ALL' },
                    { label: 'Manuel', value: 'MANUAL' },
                    { label: 'İçe Aktarım', value: 'IMPORT' },
                  ]}
                />
                <Select
                  mode='multiple'
                  allowClear
                  style={{ minWidth: 260 }}
                  value={campaignTagFilters}
                  onChange={setCampaignTagFilters}
                  placeholder='Tag filtrele'
                  options={campaignTagOptions.map((tag) => ({ label: tag, value: tag }))}
                />
                <Text type='secondary'>Seçili kişi: {(selectedCampaignContactIds || []).length}</Text>
              </Space>

              <Table<MailingContact>
                rowKey='id'
                size='small'
                dataSource={filteredCampaignContacts}
                pagination={{ pageSize: 8 }}
                scroll={{ y: 280 }}
                rowSelection={{
                  preserveSelectedRowKeys: true,
                  selectedRowKeys: selectedCampaignContactIds || [],
                  onChange: (selectedRowKeys) => {
                    campaignForm.setFieldsValue({ contactIds: selectedRowKeys as string[] });
                  },
                }}
                columns={[
                  {
                    title: 'Ad Soyad',
                    key: 'name',
                    render: (_: unknown, record) => formatContactName(record.firstName, record.lastName),
                  },
                  { title: 'E-posta', dataIndex: 'email', key: 'email' },
                  { title: 'Telefon', dataIndex: 'phone', key: 'phone', render: (value?: string | null) => value || '-' },
                  { title: 'Firma', dataIndex: 'companyName', key: 'companyName', render: (value?: string | null) => value || '-' },
                  {
                    title: 'Kaynak',
                    dataIndex: 'source',
                    key: 'source',
                    render: (source?: string | null) => (
                      source === 'IMPORT' ? <Tag color='processing'>{getMailingSourceLabel(source)}</Tag> : <Tag>{getMailingSourceLabel(source)}</Tag>
                    ),
                  },
                  {
                    title: 'Tag',
                    dataIndex: 'tags',
                    key: 'tags',
                    render: (tags?: string[] | null) =>
                      Array.isArray(tags) && tags.length > 0 ? (
                        <Space size={4} wrap>
                          {tags.map((tag) => (
                            <Tag key={tag}>{tag}</Tag>
                          ))}
                        </Space>
                      ) : (
                        '-'
                      ),
                  },
                ]}
              />
            </Space>
          </Form.Item>
          <Form.Item
            name='delayMinutes'
            label='Gecikme (dakika)'
            rules={[{ required: true, message: 'Gecikme zorunludur' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MailingAdmin;
